import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useXAxisScale, useYAxisScale } from 'recharts';
import { useI18nContext } from '@/i18n/i18n-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { bucketStartIso, type ChartGranularity } from '@/lib/chart-axis';
import { chartColor } from '@/lib/chart-colors';
import {
  markerLabel,
  visibleMarkerLabels,
  type MarkerCampaign,
} from '@/lib/campaign-markers';
import { cn } from '@/lib/utils';

export type ChartCampaignMarkersProps<C extends MarkerCampaign> = {
  /** The chart rows (`month` = X category, `date` = bucket start). */
  data: Record<string, unknown>[];
  /** From `groupCampaignsByBucket`, keyed by `bucketStartIso`. */
  groups: Map<string, C[]>;
  granularity: ChartGranularity;
  /** Row value the dot sits on (falls back to `${valueKey}Partial`). */
  valueKey: string;
  /** Name / "N campanhas" label above each dot (desktop only; HOME-04). */
  labels?: boolean;
  /** Bar charts: the dot goes at the middle of the band. */
  scale?: 'point' | 'band';
  /** A marker was clicked/tapped or focused; receives the row index. */
  onMarkerActivate?: (index: number) => void;
};

type Marker<C> = {
  index: number;
  x: number;
  y: number;
  list: C[];
  axisLabel: string;
  /** Desktop label text (`null` without labels). */
  text: string | null;
};

const DOT_R = 4.5;
const DOT_COUNT_R = 8;
/** The label's height and its gap above the dot (`h-[22px]`, `+12px`). */
const LABEL_HEIGHT = 22;
const LABEL_LIFT = 12;
const NO_HIDDEN_LABELS: ReadonlySet<string> = new Set();

/** A label is hidden for this marker and this text (`index:text`). */
const labelKey = (index: number, text: string) => `${index}:${text}`;

const sameKeys = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
  a.size === b.size && [...a].every((key) => b.has(key));

const numberAt = (row: Record<string, unknown>, key: string) => {
  const value = row[key] ?? row[`${key}Partial`];
  return typeof value === 'number' ? value : null;
};

/**
 * Campaign markers of a time-series chart (HOME-04): a blue dot on each
 * bucket that had campaigns (the count inside for 2+). Each marker also gets
 * a real `<button>`, laid over the chart in HTML, for focus, touch and the
 * screen reader; on desktop with `labels` the button is the visible label
 * above the dot. When two labels would overlap, the most recent one stays
 * and the neighbour's button shrinks back onto its dot (still focusable and
 * clickable).
 */
// SPEC_DEVIATION: the design draws the desktop label in SVG (text over a
// `rect`) with a transparent HTML button on top; here the label is the HTML
// button itself. Reason: one element for the text and the hit target, and an
// SVG `rect` would need the text measured to fit it.
export function ChartCampaignMarkers<C extends MarkerCampaign>({
  data,
  groups,
  granularity,
  valueKey,
  labels = false,
  scale = 'point',
  onMarkerActivate,
}: ChartCampaignMarkersProps<C>) {
  const { LL, locale } = useI18nContext();
  const isMobile = useIsMobile();
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  const layerRef = useRef<SVGGElement>(null);
  const [overlay, setOverlay] = useState<HTMLElement | null>(null);
  const buttonsRef = useRef(new Map<number, HTMLButtonElement>());
  /** Width of each label text once drawn, to place it while hidden. */
  const labelWidthsRef = useRef(new Map<string, number>());
  const [hiddenLabels, setHiddenLabels] = useState(NO_HIDDEN_LABELS);

  useLayoutEffect(() => {
    const wrapper = layerRef.current?.closest('.recharts-wrapper');
    setOverlay(wrapper instanceof HTMLElement ? wrapper : null);
  }, []);

  const showLabels = labels && !isMobile;
  const color = chartColor(1);

  const markers = useMemo(() => {
    const list: Marker<C>[] = [];
    if (!xScale || !yScale) return list;
    data.forEach((row, index) => {
      if (typeof row.date !== 'string') return;
      const key = bucketStartIso(row.date, granularity, locale);
      const campaigns = key ? groups.get(key) : undefined;
      const value = numberAt(row, valueKey);
      if (!campaigns?.length || value === null) return;
      const x = xScale(row.month, {
        position: scale === 'band' ? 'middle' : undefined,
      });
      const y = yScale(value);
      if (x === undefined || y === undefined) return;
      list.push({
        index,
        x,
        y,
        list: campaigns,
        axisLabel: String(row.month ?? ''),
        text: showLabels ? markerLabel(campaigns, LL) : null,
      });
    });
    return list;
  }, [
    xScale,
    yScale,
    data,
    groups,
    granularity,
    locale,
    valueKey,
    scale,
    showLabels,
    LL,
  ]);

  // Measures the drawn labels, then hides the ones that collide (HOME-04).
  // Also after the buttons first land in the overlay.
  useLayoutEffect(() => {
    if (!showLabels || !overlay) return;
    const widths = labelWidthsRef.current;
    for (const button of buttonsRef.current.values()) {
      const text = button.dataset.chartCampaignLabel;
      if (text !== undefined) widths.set(text, button.offsetWidth);
    }
    const boxes = markers.flatMap(({ index, x, y, text }) => {
      const width = text === null ? undefined : widths.get(text);
      if (text === null || width === undefined) return [];
      const bottom = y - LABEL_LIFT;
      return [
        {
          index,
          text,
          left: x - width / 2,
          right: x + width / 2,
          top: bottom - LABEL_HEIGHT,
          bottom,
        },
      ];
    });
    const visible = visibleMarkerLabels(boxes);
    const next = new Set(
      boxes
        .filter(({ index }) => !visible.has(index))
        .map(({ index, text }) => labelKey(index, text))
    );
    setHiddenLabels((previous) => (sameKeys(previous, next) ? previous : next));
  }, [showLabels, overlay, markers]);

  return (
    <g ref={layerRef} className="recharts-campaign-markers">
      {markers.map(({ index, x, y, list }) => {
        const multi = list.length > 1;
        return (
          <g key={index} data-chart-campaign-marker>
            <circle
              cx={x}
              cy={y}
              r={multi ? DOT_COUNT_R : DOT_R}
              fill={color}
              stroke="hsl(var(--background))"
              strokeWidth={2}
            />
            {multi ? (
              <text
                x={x}
                y={y}
                dy="0.35em"
                textAnchor="middle"
                fontSize={10}
                fontWeight={700}
                fill="hsl(var(--background))"
              >
                {list.length}
              </text>
            ) : null}
          </g>
        );
      })}
      {overlay
        ? createPortal(
            markers.map(({ index, x, y, list, axisLabel, text }) => {
              const multi = list.length > 1;
              const label =
                text !== null && !hiddenLabels.has(labelKey(index, text))
                  ? text
                  : null;
              const ariaLabel = multi
                ? LL.chartCampaigns.ariaMany({
                    count: list.length,
                    date: axisLabel,
                  })
                : LL.chartCampaigns.ariaOne({
                    name: list[0].name,
                    date: axisLabel,
                  });
              const activate = () => onMarkerActivate?.(index);
              return (
                <button
                  key={index}
                  ref={(button) => {
                    if (button) buttonsRef.current.set(index, button);
                    else buttonsRef.current.delete(index);
                  }}
                  type="button"
                  aria-label={ariaLabel}
                  data-chart-campaign-button
                  data-chart-campaign-label={label ?? undefined}
                  onClick={(event) => {
                    // Not a click on the bucket (no drill-down).
                    event.stopPropagation();
                    activate();
                  }}
                  onFocus={activate}
                  className={cn(
                    'absolute z-10 -translate-x-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    label !== null
                      ? 'flex h-[22px] -translate-y-[calc(100%+12px)] items-center whitespace-nowrap rounded-sm bg-background px-1.5 text-[11px] font-semibold text-[hsl(var(--chart-1))] shadow-[0_0_0_1px_hsl(var(--chart-1)/0.35)]'
                      : 'size-6 -translate-y-1/2 rounded-full'
                  )}
                  style={{ left: x, top: y }}
                >
                  {label}
                </button>
              );
            }),
            overlay
          )
        : null}
    </g>
  );
}
